import { useRef, useState, useEffect } from 'react';
import { PencilIcon, ArrowPathIcon, CheckIcon } from '@heroicons/react/24/outline';

interface SignatureCaptureProps {
  value?: string;
  onChange: (signature: string) => void;
  disabled?: boolean;
}

export function SignatureCapture({ value, onChange, disabled = false }: SignatureCaptureProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [showPad, setShowPad] = useState(false);
  const [isEmpty, setIsEmpty] = useState(true);

  useEffect(() => {
    if (showPad && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');

      if (ctx) {
        // Set canvas size
        canvas.width = canvas.offsetWidth;
        canvas.height = canvas.offsetHeight;

        // Set drawing style
        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';

        // Clear canvas
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Draw placeholder text
        if (isEmpty) {
          ctx.font = '16px sans-serif';
          ctx.fillStyle = '#9ca3af';
          ctx.textAlign = 'center';
          ctx.fillText('Sign here', canvas.width / 2, canvas.height / 2);
        }
      }
    }
  }, [showPad, isEmpty]);

  const getCoordinates = (e: React.MouseEvent | React.TouchEvent): { x: number; y: number } | null => {
    const canvas = canvasRef.current;
    if (!canvas) return null;

    const rect = canvas.getBoundingClientRect();

    if ('touches' in e) {
      if (e.touches.length === 0) return null;
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top,
      };
    } else {
      return {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };
    }
  };

  const startDrawing = (e: React.MouseEvent | React.TouchEvent) => {
    if (disabled) return;

    const coords = getCoordinates(e);
    if (!coords) return;

    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx || !canvas) return;

    // Clear placeholder on first draw
    if (isEmpty) {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      setIsEmpty(false);
    }

    ctx.beginPath();
    ctx.moveTo(coords.x, coords.y);
    setIsDrawing(true);

    e.preventDefault();
  };

  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawing || disabled) return;

    const coords = getCoordinates(e);
    if (!coords) return;

    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;

    ctx.lineTo(coords.x, coords.y);
    ctx.stroke();

    e.preventDefault();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx || !canvas) return;

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setIsEmpty(true);
  };

  const saveSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas || isEmpty) return;

    const dataUrl = canvas.toDataURL('image/png');
    onChange(dataUrl);
    setShowPad(false);
  };

  const cancelSignature = () => {
    clearSignature();
    setShowPad(false);
  };

  return (
    <div>
      {/* Existing Signature */}
      {value && !showPad && (
        <div className="mb-4">
          <img
            src={value}
            alt="Signature"
            className="border border-gray-300 rounded-lg p-2 bg-white max-w-xs"
          />
        </div>
      )}

      {/* Signature Pad */}
      {showPad && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Add Signature</h3>

            <div className="border-2 border-gray-300 rounded-lg bg-white mb-4">
              <canvas
                ref={canvasRef}
                className="w-full touch-none cursor-crosshair"
                style={{ height: '300px' }}
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={stopDrawing}
              />
            </div>

            <div className="flex justify-between gap-3">
              <button
                type="button"
                onClick={clearSignature}
                className="btn btn-secondary flex items-center"
              >
                <ArrowPathIcon className="w-5 h-5 mr-2" />
                Clear
              </button>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={cancelSignature}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={saveSignature}
                  disabled={isEmpty}
                  className="btn btn-primary flex items-center"
                >
                  <CheckIcon className="w-5 h-5 mr-2" />
                  Save Signature
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      {!disabled && (
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => setShowPad(true)}
            className="btn btn-secondary flex items-center"
          >
            <PencilIcon className="w-5 h-5 mr-2" />
            {value ? 'Update Signature' : 'Add Signature'}
          </button>
          {value && (
            <button
              type="button"
              onClick={() => onChange('')}
              className="btn btn-secondary text-red-600 hover:text-red-700"
            >
              Remove
            </button>
          )}
        </div>
      )}

      {/* Status */}
      {value && !showPad && (
        <p className="text-sm text-green-600 mt-2 flex items-center gap-1">
          <CheckIcon className="w-4 h-4" />
          Signature captured
        </p>
      )}
    </div>
  );
}
